import AIButton from 'ai-components-ui-library/components/buttons/AIButton';
import { useOpenCopilot } from 'copilot-toolkit/hooks/useOpenCopilot';
import { RemoveIcon } from 'foundations-assets/icons/components/RemoveIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { useUsageTracker } from 'marketplace-ui-tracking';
import { useLocation } from 'quartz-routing/react-router';
import styled from 'styled-components';
import { Paths } from '../../constants/paths';
import { useAppContext } from '../../context/AppContext';
import { useGates } from '../../hooks/useGates';
import { useFilterConfig } from '../hooks/useFilterConfig';
import { AgentFilterControls } from './AgentFilterControls';
import { AppFilterControls } from './AppFilterControls';
import { SelectFilter } from './reusableFilters/SelectFilter';
import { PortalInstallableFilter } from './singleUseFilters/PortalInstallableFilter';
import { SolutionsPartnerFilterControls } from './SolutionsPartnerFilterControls';
import { WorkflowTemplateFilterControls } from './WorkflowTemplateFilterControls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledAside = styled.aside.withConfig({
  displayName: "FilterPanel__StyledAside"
})(({
  theme: _theme,
  $open
}) => {
  const theme = _theme;
  return {
    marginInlineStart: $open ? theme.space['600'] : '0',
    opacity: $open ? 1 : 0,
    transition: 'opacity 0.3s linear'
  };
});
function AskBreezeButton({
  onClose
}) {
  const openCopilot = useOpenCopilot();
  const appContext = useAppContext();
  const {
    breezeAssistant
  } = useGates();
  const usageTracker = useUsageTracker();
  if (!breezeAssistant || appContext === 'public') return null;
  return /*#__PURE__*/_jsx(AIButton, {
    use: "transparent",
    block: true,
    marginBlockEnd: "200",
    onClick: () => {
      onClose === null || onClose === void 0 || onClose();
      openCopilot();
    },
    usageTracker: usageTracker,
    featureId: "marketplace-storefront-filter-with-breeze",
    featureCategoryId: "marketplace-storefront-breeze-assistant",
    tooltip: unescapedText('marketplaceStorefront.interactiveMode.buttonTooltip'),
    children: unescapedText('marketplaceStorefront.interactiveMode.buttonText')
  });
}
export function FilterPanel({
  open,
  onClose
}) {
  const {
    mobile,
    mobilePanelWidth
  } = useResponsiveProps({
    mobile: {
      xs: true,
      lg: false
    },
    mobilePanelWidth: {
      xs: undefined,
      sm: '400px'
    }
  });
  const searchParams = useSearchParams();
  const isWorkflowTemplate = searchParams.get('PRODUCT_TYPE') === 'WORKFLOW_TEMPLATE';
  if (mobile) {
    return open && /*#__PURE__*/_jsxs(ModalPanel, {
      width: mobilePanelWidth,
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "heading-300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplaceStorefront.filters.panelTitle"
          })
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: onClose
        })]
      }), /*#__PURE__*/_jsxs(DialogBody, {
        paddingBlockEnd: "1000",
        children: [/*#__PURE__*/_jsx(AskBreezeButton, {
          onClose: onClose
        }), /*#__PURE__*/_jsx(FilterPanelContent, {})]
      }), /*#__PURE__*/_jsx(DialogFooter, {
        children: /*#__PURE__*/_jsx(Button, {
          onClick: onClose,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplaceStorefront.filters.closePanel"
          })
        })
      })]
    });
  }
  const topMargin = searchParams.get('search') ? isWorkflowTemplate ? '56px' : '70px' : '0px';
  return /*#__PURE__*/_jsxs(StyledAside, {
    $open: open,
    children: [open && /*#__PURE__*/_jsx(View, {
      blockSize: topMargin,
      inlineSize: "100%"
    }), open && /*#__PURE__*/_jsx(CardWrapper, {
      compact: true,
      display: "flex",
      flexDirection: "column",
      gap: "400",
      inlineSize: "302px",
      tabIndex: -1,
      children: /*#__PURE__*/_jsxs(CardSection, {
        children: [/*#__PURE__*/_jsxs(Flex, {
          justifyContent: "space-between",
          marginBlockEnd: "200",
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "heading-300",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplaceStorefront.filters.panelTitle"
            })
          }), /*#__PURE__*/_jsx(IconButton, {
            onClick: onClose,
            use: "transparent",
            padding: "0",
            marginInlineEnd: "-200",
            children: /*#__PURE__*/_jsx(RemoveIcon, {
              size: "300"
            })
          })]
        }), /*#__PURE__*/_jsx(AskBreezeButton, {}), /*#__PURE__*/_jsx(FilterPanelContent, {})]
      })
    })]
  });
}
function FilterPanelContent() {
  const {
    PRODUCT_TYPE,
    INSTALLED
  } = useFilterConfig();
  const location = useLocation();
  const hideProductTypeFilter = location.pathname === Paths.SEARCH_RESULTS;
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "300",
    children: [/*#__PURE__*/_jsx(PortalInstallableFilter, {}), !hideProductTypeFilter && /*#__PURE__*/_jsx(SelectFilter, {
      filter: PRODUCT_TYPE
    }), /*#__PURE__*/_jsx(SelectFilter, {
      filter: INSTALLED
    }), /*#__PURE__*/_jsx(AppFilterControls, {}), /*#__PURE__*/_jsx(AgentFilterControls, {}), /*#__PURE__*/_jsx(WorkflowTemplateFilterControls, {}), /*#__PURE__*/_jsx(SolutionsPartnerFilterControls, {})]
  });
}